import type { Observer } from '@naturehouse/nh-essentials/lib/architecture/ObserverPattern';
import ApiRequestState from '../../../common/search/store/ApiRequestState';
import ArrivalDepartureFilter from '../store/ArrivalDepartureFilter';
import HouseSearchFilters from '../store/HouseSearchFilters';
import GTM from '../../../gtm/GTM';
import GTMOverviewFilterAddModel from '../../../gtm/models/GTMOverviewFilterAddModel';
import PriceFilter from '../store/PriceFilter';

interface PriceRangeContextInterface extends ContextInterface {
    element: HTMLElement;
    bindings: StandardObjectInterface;
}

export default class PriceRange implements Observer {
    private readonly context: PriceRangeContextInterface;

    private readonly minPriceField: HTMLSelectElement;

    private readonly maxPriceField: HTMLSelectElement;

    readonly #dateFilter: ArrivalDepartureFilter;

    constructor(context: PriceRangeContextInterface) {
        this.context = context;

        this.minPriceField = this.context.element.querySelector(
            '[name="minPrice"]'
        ) as HTMLSelectElement;
        this.maxPriceField = this.context.element.querySelector(
            '[name="maxPrice"]'
        ) as HTMLSelectElement;

        this.#dateFilter = HouseSearchFilters.getInstance().get(
            ArrivalDepartureFilter.NAME
        ) as ArrivalDepartureFilter;
    }

    isValid(): boolean {
        return !(!this.minPriceField || !this.maxPriceField);
    }

    onInit(): void {
        if (!this.isValid()) {
            return;
        }

        ApiRequestState.getInstance().attach(this);
        this.update();

        this.minPriceField.addEventListener('change', () => {
            this.#trackMinPriceChangeEvent();
            this.disableInvalidFields(this.minPriceField, this.minPriceField.value);
        });

        this.maxPriceField.addEventListener('change', () => {
            this.#trackMaxPriceChangeEvent();
            this.disableInvalidFields(this.maxPriceField, this.maxPriceField.value);
        });
    }

    #trackMinPriceChangeEvent(): void {
        GTM.track(
            GTMOverviewFilterAddModel.parseToGTMData({
                label: this.minPriceField.value,
                category: PriceFilter.MIN_NAME
            })
        );
    }

    #trackMaxPriceChangeEvent(): void {
        GTM.track(
            GTMOverviewFilterAddModel.parseToGTMData({
                label: this.maxPriceField.value,
                category: PriceFilter.MAX_NAME
            })
        );
    }

    disableInvalidFields(field: HTMLSelectElement, targetValue: string): void {
        if (field === this.minPriceField) {
            const options: HTMLOptionElement[] = Array.from(this.maxPriceField.options);
            options.forEach((opt: HTMLOptionElement) => {
                const option = opt;
                if (option.value === '') {
                    return;
                }

                const optionValue = Number(option.value);
                const value = Number(targetValue);
                if (optionValue <= value) {
                    option.disabled = true;
                    return;
                }

                option.disabled = false;
            });

            return;
        }

        if (field === this.maxPriceField) {
            const options: HTMLOptionElement[] = Array.from(this.minPriceField.options);
            options.forEach((opt: HTMLOptionElement) => {
                const option = opt;
                if (targetValue === '') {
                    option.disabled = false;
                    return;
                }

                const optionValue = Number(option.value);
                const value = Number(targetValue);
                if (optionValue >= value) {
                    option.disabled = true;
                    return;
                }

                option.disabled = false;
            });
        }
    }

    static getClassName(): string {
        return 'PriceRange';
    }

    update(): void {
        const params = this.#dateFilter.parameters;
        const hasDateRange = params.arrivalDate !== null && params.departureDate !== null;
        this.context.element.classList.toggle('hidden', !hasDateRange);
    }
}
